import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/utils/supabase';
import { ProspectLoan, ProspectFilters, ProspectSortConfig } from './types';
import { createComponentLogger } from '@/utils/logger';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { normalizeSearchQuery } from './search-utils';
import { matchesSegment } from './activeLoanModel';

const log = createComponentLogger('usePaginatedProspects', 'encompass-prospects');

const PAGE_SIZE = 50;

export type FetchMode = 'paginated' | 'full';

export interface ProspectRefreshOptions {
  refetchOnWindowFocus?: boolean;
  refreshIntervalMs?: number;
}

/**
 * Pure filter function used by the realtime subscription to check if a loan
 * matches the current filter state. Exported for direct testing.
 */
export function matchesProspectFilter(loan: Partial<ProspectLoan>, f: Partial<ProspectFilters>): boolean {
  if (loan.deleted_from_encompass) {
return false;
}

  // Folder filter
  if (f.loanFolder && f.loanFolder !== 'all') {
    if (loan.loan_folder !== f.loanFolder) {
return false;
}
  }

  // Excluded folders
  if (f.excludedFolders && f.excludedFolders.length > 0) {
    if (loan.loan_folder && f.excludedFolders.includes(loan.loan_folder)) {
return false;
}
  }

  // Loan officer filter
  if (f.loanOfficerId && f.loanOfficerId !== 'all') {
    if (f.loanOfficerId === 'unassigned') {
      if (loan.loan_officer_id) {
return false;
}
    } else {
      if (loan.loan_officer_id !== f.loanOfficerId) {
return false;
}
    }
  }

  // Loan purpose filter
  if (f.loanPurpose && f.loanPurpose !== 'all') {
    if (loan.trans_details_loan_purpose !== f.loanPurpose) {
return false;
}
  }

  // Date range filters
  if (f.createdAfter) {
    const loanDate = loan.creation_date ? parseAsLocalDate(loan.creation_date) : null;
    const filterDate = parseAsLocalDate(f.createdAfter);
    if (!loanDate || loanDate < filterDate) {
return false;
}
  }
  if (f.createdBefore) {
    const loanDate = loan.creation_date ? parseAsLocalDate(loan.creation_date) : null;
    const filterDate = parseAsLocalDate(f.createdBefore);
    if (!loanDate || loanDate > filterDate) {
return false;
}
  }

  // Amount range filters — coerce with Number() so Realtime string values
  // don't produce lexicographic comparison instead of numeric
  if (f.minAmount !== undefined) {
    const amt = loan.trans_details_loan_amt !== null ? Number(loan.trans_details_loan_amt) : null;
    if (amt === null || isNaN(amt) || amt < f.minAmount) {
return false;
}
  }
  if (f.maxAmount !== undefined) {
    const amt = loan.trans_details_loan_amt !== null ? Number(loan.trans_details_loan_amt) : null;
    if (amt === null || isNaN(amt) || amt > f.maxAmount) {
return false;
}
  }

  // Excluded milestones
  if (f.excludedMilestones && f.excludedMilestones.length > 0) {
    if (loan.milestone && f.excludedMilestones.includes(loan.milestone)) {
return false;
}
  }

  // Excluded channels
  if (f.excludedChannels && f.excludedChannels.length > 0) {
    if (loan.channel && f.excludedChannels.includes(loan.channel)) {
return false;
}
  }

  // Pipeline filter
  if (f.pipeline === 'post_closing') {
    if (!loan.funded_date) {
return false;
}
    if (!['Banked - Retail', 'Banked - Wholesale'].includes(loan.channel || '')) {
return false;
}
  }

  // MERS Transfer Date Audit: all purchased loans (purchased_date set)
  if (f.pipeline === 'mers_audit') {
    if (!loan.purchased_date) {
return false;
}
  }

  // Lock status filter
  if (f.lockStatus === 'Locked') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lockExpires = loan.rate_lock_buy_side_lock_expires ? parseAsLocalDate(loan.rate_lock_buy_side_lock_expires) : null;
    const investorDelivery = loan.shipping_investor_delivery_date ? parseAsLocalDate(loan.shipping_investor_delivery_date) : null;
    const isCurrentlyLocked = loan.rate_lock_status === 'Locked' && lockExpires && lockExpires >= today;
    const isLockMet = investorDelivery && lockExpires && investorDelivery <= lockExpires;
    if (!isCurrentlyLocked && !isLockMet) {
return false;
}
  } else if (f.lockStatus === 'Expired') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lockExpires = loan.rate_lock_buy_side_lock_expires ? parseAsLocalDate(loan.rate_lock_buy_side_lock_expires) : null;
    const investorDelivery = loan.shipping_investor_delivery_date ? parseAsLocalDate(loan.shipping_investor_delivery_date) : null;
    const isLockExpired = lockExpires && lockExpires < today;
    const isLockMet = investorDelivery && lockExpires && investorDelivery <= lockExpires;
    if (!isLockExpired || isLockMet) {
return false;
}
  } else if (f.lockStatus === 'NotLocked') {
    if (loan.rate_lock_buy_side_lock_expires) {
return false;
}
  }

  // Delivery status filter
  if (f.deliveryStatus === 'FundedNotDelivered') {
    if (!loan.funded_date || !loan.ready_to_ship_date || loan.shipping_investor_delivery_date) {
return false;
}
  } else if (f.deliveryStatus === 'FundedNotReadyToShip') {
    if (!loan.funded_date || loan.ready_to_ship_date || loan.shipping_investor_delivery_date) {
return false;
}
  } else if (f.deliveryStatus === 'Shipped') {
    if (!loan.funded_date || !loan.shipping_actual_ship_date || loan.shipping_investor_delivery_date) {
return false;
}
  } else if (f.deliveryStatus === 'Delivered') {
    if (!loan.shipping_investor_delivery_date) {
return false;
}
  }

  // Segment filter (active Loan Center redesign). Delegates to matchesSegment so
  // the client realtime predicate stays identical to the SQL `p_segment` branch.
  // 'all' and 'avg' are no-ops (matchesSegment returns true). Parity is asserted
  // by the segment-predicate parity test.
  if (!matchesSegment(loan as ProspectLoan, f.segment)) {
return false;
}

  // Search query — fields must stay aligned with search_encompass_loans RPC
  const searchQuery = normalizeSearchQuery(f.searchQuery);
  if (searchQuery) {
    const loanNumber = loan.trans_details_loan_number?.toLowerCase() || '';
    const borrowerFirst = loan.borrower_first_name?.toLowerCase() || '';
    const borrowerLast = loan.borrower_last_name?.toLowerCase() || '';
    const address = loan.property_address?.toLowerCase() || '';
    const investorName = loan.investor_name?.toLowerCase() || '';
    const investorLoanNumber = loan.rate_lock_investor_commitment?.toLowerCase() || '';
    const loFirst = loan.loan_officer_first_name?.toLowerCase() || '';
    const loLast = loan.loan_officer_last_name?.toLowerCase() || '';
    const loFullName = [loFirst, loLast].filter(Boolean).join(' ');
    const loReversed = [loLast, loFirst].filter(Boolean).join(' ');
    const matchesSearch =
      loanNumber.includes(searchQuery) ||
      borrowerFirst.includes(searchQuery) ||
      borrowerLast.includes(searchQuery) ||
      address.includes(searchQuery) ||
      investorName.includes(searchQuery) ||
      investorLoanNumber.includes(searchQuery) ||
      loFirst.includes(searchQuery) ||
      loLast.includes(searchQuery) ||
      loFullName.includes(searchQuery) ||
      loReversed.includes(searchQuery);
    if (!matchesSearch) {
return false;
}
  }

  return true;
}

/**
 * Pure function that resolves how the Realtime subscription should handle an
 * UPDATE event. Exported for direct testing — the subscription callback
 * dispatches on the returned action.
 *
 * Key invariant: we never inline-replace a loan with raw Realtime data because
 * Supabase delivers numeric columns as strings, corrupting downstream math.
 */
export type RealtimeUpdateAction =
  | { type: 'refetch' }
  | { type: 'remove'; loanId: string }
  | { type: 'noop' };

export function resolveRealtimeUpdate(
  newLoan: Partial<ProspectLoan>,
  currentIds: Set<string>,
  matchesFilter: (loan: ProspectLoan) => boolean
): RealtimeUpdateAction {
  // Empty/oversized payload guard
  if (!newLoan || !newLoan.id) {
    return { type: 'refetch' };
  }

  const wasInList = currentIds.has(newLoan.id);
  const shouldBeInList = matchesFilter(newLoan as ProspectLoan);

  if (wasInList && shouldBeInList) {
    // Schedule authoritative RPC refetch instead of inline replacement
    return { type: 'refetch' };
  }
  if (wasInList && !shouldBeInList) {
    return { type: 'remove', loanId: newLoan.id };
  }
  if (!wasInList && shouldBeInList) {
    return { type: 'refetch' };
  }
  return { type: 'noop' };
}

// Map old field names to new database column names for sorting
const FIELD_NAME_MAP: Record<string, string> = {
  loan_number: 'trans_details_loan_number',
  loan_amount: 'trans_details_loan_amt',
  loan_purpose: 'trans_details_loan_purpose',
  loan_status: 'trans_details_loan_status',
  property_city: 'subject_property_city',
  property_state: 'subject_property_state',
  created_at: 'creation_date'
};

interface SearchResponse {
  loans: ProspectLoan[];
  totalCount: number;
  hasMore: boolean;
}

/**
 * Resolves the `p_pipeline` value sent to search_encompass_loans. The MERS audit
 * blank/transferred sub-filter is encoded into p_pipeline HERE — the only place
 * the suffixed value exists. UI state stays `pipeline === 'mers_audit'` +
 * `mersStatus`; never set the suffixed value on `filters.pipeline`.
 */
export function resolvePipelineParam(f: Partial<ProspectFilters>): string | null {
  if (f.pipeline === 'mers_audit' && f.mersStatus && f.mersStatus !== 'all') {
    return `mers_audit_${f.mersStatus}`; // mers_audit_blank | mers_audit_transferred
  }
  return f.pipeline || null;
}

/**
 * Fetch the COMPLETE result set for the given filters/sort by paging on the
 * RPC's own `hasMore` contract (page size 1000) until exhausted. Stateless —
 * does not touch hook state. Used by the MERS audit CSV export so the export
 * isn't limited to the rows scrolled into view. Mirrors the hook's RPC params.
 */
export async function fetchAllProspects(
  organizationId: string,
  filters: ProspectFilters,
  sortConfig: ProspectSortConfig,
  includeTransferred = false
): Promise<ProspectLoan[]> {
  const EXPORT_PAGE_SIZE = 1000;
  const MAX_PAGES = 100; // backstop: caps a runaway loop at 100k rows
  const sortField = FIELD_NAME_MAP[sortConfig.field] || sortConfig.field;
  const all: ProspectLoan[] = [];
  let offset = 0;

  for (let page = 0; page < MAX_PAGES; page++) {
    const { data, error } = await supabase.rpc('search_encompass_loans', {
      p_organization_id: organizationId,
      p_search_query: normalizeSearchQuery(filters.searchQuery) || null,
      p_loan_folder: filters.loanFolder || null,
      p_loan_officer_id: filters.loanOfficerId || null,
      p_loan_purpose: filters.loanPurpose || null,
      p_created_after: filters.createdAfter || null,
      p_created_before: filters.createdBefore || null,
      p_min_amount: filters.minAmount || null,
      p_max_amount: filters.maxAmount || null,
      p_sort_field: sortField,
      p_sort_direction: sortConfig.direction,
      p_limit: EXPORT_PAGE_SIZE,
      p_offset: offset,
      p_include_transferred: includeTransferred,
      p_excluded_folders: filters.excludedFolders?.length ? filters.excludedFolders : null,
      p_excluded_milestones: filters.excludedMilestones?.length ? filters.excludedMilestones : null,
      p_excluded_channels: filters.excludedChannels?.length ? filters.excludedChannels : null,
      p_pipeline: resolvePipelineParam(filters),
      p_lock_status: filters.lockStatus || null,
      p_delivery_status: filters.deliveryStatus || null,
      p_queue_stage: filters.queueStage || null,
      p_segment: filters.segment && filters.segment !== 'all' && filters.segment !== 'avg'
        ? filters.segment
        : null
    });
    if (error) {
      throw new Error(error.message);
    }
    const response: SearchResponse = typeof data === 'string' ? JSON.parse(data) : (data as SearchResponse);
    const loans = response?.loans || [];
    all.push(...loans);
    offset += loans.length;
    if (!response?.hasMore || loans.length === 0) {
      break;
    }
  }
  return all;
}

export interface UsePaginatedProspectsReturn {
  prospects: ProspectLoan[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  totalCount: number;
  /** Incremented on each successful fetch. Watch this to sync dependent queries. */
  dataVersion: number;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  updateProspect: (loanId: string, updates: Partial<ProspectLoan>) => void;
}

export function usePaginatedProspects(
  organizationId: string | undefined,
  filters: ProspectFilters = {},
  sortConfig: ProspectSortConfig = { field: 'creation_date', direction: 'desc' },
  includeTransferred: boolean = false,
  fetchMode: FetchMode = 'paginated',
  refreshOptions: ProspectRefreshOptions = {}
): UsePaginatedProspectsReturn {
  const [prospects, setProspects] = useState<ProspectLoan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);

  // Incremented on each successful fetch (user-initiated or realtime-triggered).
  // Consumers can watch this to know when the table data has been refreshed.
  const [dataVersion, setDataVersion] = useState(0);

  // Track search query changes for debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSearchRef = useRef<string>('');

  // Track latest fetchProspects to avoid stale closures in debounced setTimeout
  const fetchProspectsRef = useRef<((loadMore?: boolean, currentOffset?: number, silent?: boolean) => Promise<void>) | null>(null);

  // Request ID counter to prevent stale RPC responses from overwriting newer data
  const requestIdRef = useRef(0);

  // Track the request ID of the latest user-initiated (non-silent) fetch
  // 0 = no user fetch in-flight, >0 = requestId of the latest non-silent fetch
  // Using request ID instead of boolean prevents stale fetches from clearing the flag
  // while a newer non-silent fetch is still active
  const userFetchInFlightRef = useRef(0);

  // Track whether a realtime refetch was deferred while a user fetch was in-flight
  // When set, the deferred refetch will be replayed after the user fetch completes
  const pendingRealtimeRefetchRef = useRef(false);

  // Ref to hold current filters for use in subscription callback (avoids stale closures)
  const filtersRef = useRef(filters);

  // Refs to hold current offset and prospects for silent refresh pagination preservation
  const offsetRef = useRef(offset);
  const prospectsRef = useRef(prospects);

  // Track fetchMode transitions so the reset effect can clear visible state
  // BEFORE the next fetch settles. Without this, switching Pipeline/Hybrid → Table
  // would briefly render the stale full-loaded dataset dimmed inside the table —
  // violating the approved reset-semantics rule.
  const prevFetchModeRef = useRef(fetchMode);

  // Coalescing refs for realtime refetches - debounce + maxWait + inFlight guard
  const realtimeRefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeRefetchFirstCallRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const REALTIME_DEBOUNCE_MS = 500;
  const REALTIME_MAX_WAIT_MS = 2000;

  // Coalesced realtime refetch - debounce 500ms + maxWait 2s + inFlight guard
  // Prevents rapid webhook updates from causing fetch storms while ensuring updates aren't delayed indefinitely
  // Defined before fetchProspects so it can be called from the user-fetch completion replay paths
  const scheduleRealtimeRefetch = useCallback(() => {
    const now = Date.now();

    // Clear existing debounce timeout
    if (realtimeRefetchTimeoutRef.current) {
      clearTimeout(realtimeRefetchTimeoutRef.current);
    }

    // Check maxWait - if we've been debouncing for too long, force immediate refresh
    if (realtimeRefetchFirstCallRef.current > 0 &&
        now - realtimeRefetchFirstCallRef.current > REALTIME_MAX_WAIT_MS) {
      if (!inFlightRef.current && !userFetchInFlightRef.current) {
        inFlightRef.current = true;
        realtimeRefetchFirstCallRef.current = 0; // Reset for next batch
        log.info('Realtime refetch: maxWait exceeded, forcing immediate refresh');
        fetchProspectsRef.current?.(false, 0, true).finally(() => {
          inFlightRef.current = false;
        });
      } else if (userFetchInFlightRef.current) {
        // User fetch in-flight — defer realtime refetch, will be replayed after user fetch completes
        log.info('Realtime refetch: deferred, user fetch in-flight');
        pendingRealtimeRefetchRef.current = true;
        realtimeRefetchFirstCallRef.current = 0; // Reset maxWait for next attempt
      }
      return;
    }

    // Track first call timestamp for maxWait calculation
    if (realtimeRefetchFirstCallRef.current === 0) {
      realtimeRefetchFirstCallRef.current = now;
    }

    // Debounce: wait 500ms after last event before fetching
    realtimeRefetchTimeoutRef.current = setTimeout(() => {
      if (!inFlightRef.current && !userFetchInFlightRef.current) {
        inFlightRef.current = true;
        realtimeRefetchFirstCallRef.current = 0; // Reset for next batch
        log.info('Realtime refetch: debounce complete, executing silent refresh');
        fetchProspectsRef.current?.(false, 0, true).finally(() => {
          inFlightRef.current = false;
        });
      } else if (userFetchInFlightRef.current) {
        // User fetch in-flight — defer realtime refetch, will be replayed after user fetch completes
        log.info('Realtime refetch: deferred, user fetch in-flight');
        pendingRealtimeRefetchRef.current = true;
        realtimeRefetchFirstCallRef.current = 0;
      }
    }, REALTIME_DEBOUNCE_MS);
  }, []);

  const fetchProspects = useCallback(async (
    loadMore: boolean = false,
    currentOffset: number = 0,
    silent: boolean = false  // Silent mode: don't show loading state, used for background refreshes
  ) => {
    if (!organizationId) {
      ++requestIdRef.current; // Invalidate any in-flight requests
      setProspects([]);
      setIsLoading(false);
      setTotalCount(0);
      setHasMore(false);
      return;
    }

    // Helper: clear user fetch flag and replay any deferred realtime refetch
    // Only clears if this request IS the latest non-silent fetch (prevents stale
    // responses from clearing the flag while a newer user fetch is still active)
    const clearUserFetchAndReplay = (isSilent: boolean) => {
      if (!isSilent && thisRequestId === userFetchInFlightRef.current) {
        userFetchInFlightRef.current = 0;
        if (pendingRealtimeRefetchRef.current) {
          pendingRealtimeRefetchRef.current = false;
          log.info('Replaying deferred realtime refetch');
          scheduleRealtimeRefetch();
        }
      }
    };

    // Hoist thisRequestId before try so it's accessible in catch
    let thisRequestId = 0;
    try {
      // Increment request ID to track this request
      thisRequestId = ++requestIdRef.current;

      // Track user-initiated fetches so realtime refetches don't compete
      if (!silent) {
        userFetchInFlightRef.current = thisRequestId;
      }

      if (loadMore) {
        setIsLoadingMore(true);
      } else if (!silent) {
        // Only show loading state on non-silent fetches
        // Silent fetches keep existing data visible during background refresh
        setIsLoading(true);
      }
      setError(null);

      const sortField = FIELD_NAME_MAP[sortConfig.field] || sortConfig.field;

      // For silent background refreshes, preserve pagination by fetching up to current loaded amount
      // This prevents "jumping back to page 1" when user has scrolled through multiple pages
      const targetLimit = silent && prospectsRef.current.length > 0
        ? Math.max(offsetRef.current, PAGE_SIZE)
        : PAGE_SIZE;

      log.info('Fetching prospects via RPC', {
        filters,
        sortConfig,
        offset: currentOffset,
        loadMore,
        silent,
        targetLimit,
        requestId: thisRequestId
      });

      const timer = log.startTimer('fetch_paginated_prospects');

      const { data, error: rpcError } = await supabase.rpc('search_encompass_loans', {
        p_organization_id: organizationId,
        p_search_query: normalizeSearchQuery(filters.searchQuery) || null,
        p_loan_folder: filters.loanFolder || null,
        p_loan_officer_id: filters.loanOfficerId || null,
        p_loan_purpose: filters.loanPurpose || null,
        p_created_after: filters.createdAfter || null,
        p_created_before: filters.createdBefore || null,
        p_min_amount: filters.minAmount || null,
        p_max_amount: filters.maxAmount || null,
        p_sort_field: sortField,
        p_sort_direction: sortConfig.direction,
        p_limit: targetLimit,
        p_offset: currentOffset,
        p_include_transferred: includeTransferred,
        p_excluded_folders: filters.excludedFolders?.length ? filters.excludedFolders : null,
        p_excluded_milestones: filters.excludedMilestones?.length ? filters.excludedMilestones : null,
        p_excluded_channels: filters.excludedChannels?.length ? filters.excludedChannels : null,
        p_pipeline: resolvePipelineParam(filters),
        p_lock_status: filters.lockStatus || null,
        p_delivery_status: filters.deliveryStatus || null,
        p_queue_stage: filters.queueStage || null,
        // 'avg' is informational only; don't send to server.
        p_segment: filters.segment && filters.segment !== 'all' && filters.segment !== 'avg'
          ? filters.segment
          : null
      });

      // Check staleness IMMEDIATELY after await - before any state mutations
      if (thisRequestId !== requestIdRef.current) {
        log.info('Discarding stale RPC response', {
          thisRequestId,
          currentRequestId: requestIdRef.current
        });
        clearUserFetchAndReplay(silent);
        return; // Exit completely - don't touch any state
      }

      // Now safe to handle error (we know this request is current)
      if (rpcError) {
        log.error('Failed to fetch prospects', { organizationId }, new Error(rpcError.message));
        setError(new Error(rpcError.message));
        setIsLoading(false);
        setIsLoadingMore(false);
        clearUserFetchAndReplay(silent);
        return;
      }

      // Defensive parsing - handle both direct object and string JSON
      let response: SearchResponse;
      if (typeof data === 'string') {
        response = JSON.parse(data);
      } else {
        response = data as SearchResponse;
      }

      // Shape-only debug log — never the row payload: rows carry
      // conversation_logs (internal Encompass note text) since the
      // conversation-log stale basis shipped.
      log.info('RPC response shape', {
        dataType: typeof data,
        loanCount: response?.loans?.length ?? 0,
        hasMore: response?.hasMore,
        totalCount: response?.totalCount
      });

      const newLoans = response?.loans || [];

      if (loadMore) {
        setProspects(prev => [...prev, ...newLoans]);
      } else {
        setProspects(newLoans);
      }

      setTotalCount(response.totalCount);
      setHasMore(response.hasMore);
      setOffset(currentOffset + newLoans.length);
      setError(null); // Clear any previous error on success
      setDataVersion(v => v + 1);

      // Full-fetch continuation (Pipeline / Hybrid directions). Kanban column
      // distributions and hybrid summaries require the full active set, not
      // just the first PAGE_SIZE rows. Loop internally so callers don't need
      // to drive the pagination; isLoading stays true until the loop settles.
      if (fetchMode === 'full' && response.hasMore && newLoans.length > 0) {
        return fetchProspects(true, currentOffset + newLoans.length, silent);
      }

      setIsLoading(false);
      setIsLoadingMore(false);
      clearUserFetchAndReplay(silent);

      log.endTimer(timer, {
        count: newLoans.length,
        totalCount: response.totalCount,
        hasMore: response.hasMore
      });
    } catch (err) {
      // Only handle unexpected errors (parsing, etc.) - RPC errors handled above
      // Check staleness before setting error state
      if (thisRequestId !== requestIdRef.current) {
        log.info('Discarding stale error', { thisRequestId, currentRequestId: requestIdRef.current });
        clearUserFetchAndReplay(silent);
        return;
      }
      const error = err instanceof Error ? err : new Error(String(err));
      log.error('Failed to fetch prospects', { organizationId }, error);
      setError(error);
      setIsLoading(false);
      setIsLoadingMore(false);
      clearUserFetchAndReplay(silent);
    }
  }, [organizationId, filters, sortConfig, includeTransferred, fetchMode, scheduleRealtimeRefetch]);

  // Keep ref updated to latest fetchProspects to avoid stale closures
  useEffect(() => {
    fetchProspectsRef.current = fetchProspects;
  }, [fetchProspects]);

  // Keep filtersRef updated for subscription callback
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Keep offsetRef and prospectsRef updated for silent refresh pagination preservation
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    prospectsRef.current = prospects;
  }, [prospects]);

  // Track loaded encompass_loan_ids for targeted realtime refetch from related tables
  const loadedEncompassIdsRef = useRef(new Set<string>());
  useEffect(() => {
    loadedEncompassIdsRef.current = new Set(prospects.map(p => p.encompass_loan_id));
  }, [prospects]);

  // Load more function for infinite scroll
  const loadMore = useCallback(async () => {
    // Guard: Don't load more if already loading (either initial or more) or no more data
    if (isLoadingMore || isLoading || !hasMore) {
return;
}
    await fetchProspects(true, offset);
  }, [fetchProspects, isLoadingMore, isLoading, hasMore, offset]);

  // Refetch from beginning
  const refetch = useCallback(async () => {
    setOffset(0);
    await fetchProspects(false, 0);
  }, [fetchProspects]);

  // Scoped users can lose access when Encompass team assignments change. RLS
  // protects every new read, but already-rendered rows need an explicit
  // revalidation boundary because Realtime is not guaranteed to emit an UPDATE
  // that changes a row from visible to invisible for the subscriber.
  useEffect(() => {
    if (!organizationId || !refreshOptions.refetchOnWindowFocus) {
      return;
    }

    const silentRefresh = () => {
      if (document.visibilityState === 'visible') {
        void fetchProspectsRef.current?.(false, 0, true);
      }
    };

    window.addEventListener('focus', silentRefresh);
    document.addEventListener('visibilitychange', silentRefresh);
    return () => {
      window.removeEventListener('focus', silentRefresh);
      document.removeEventListener('visibilitychange', silentRefresh);
    };
  }, [organizationId, refreshOptions.refetchOnWindowFocus]);

  useEffect(() => {
    const intervalMs = refreshOptions.refreshIntervalMs ?? 0;
    if (!organizationId || intervalMs < 30_000) {
      return;
    }

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void fetchProspectsRef.current?.(false, 0, true);
      }
    }, intervalMs);
    return () => window.clearInterval(interval);
  }, [organizationId, refreshOptions.refreshIntervalMs]);

  // Debounced search effect
  useEffect(() => {
    const searchQuery = filters.searchQuery || '';

    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search query changed, debounce it
    if (searchQuery !== lastSearchRef.current) {
      lastSearchRef.current = searchQuery;

      if (searchQuery) {
        // Debounce search by 300ms - use ref to avoid stale closure
        searchTimeoutRef.current = setTimeout(() => {
          setOffset(0);
          fetchProspectsRef.current?.(false, 0);
        }, 300);
      } else {
        // No search query - fetch immediately using ref
        setOffset(0);
        fetchProspectsRef.current?.(false, 0);
      }
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters.searchQuery]);

  // Effect for non-search filter changes
  useEffect(() => {
    // Reset and refetch when filters (except search), sort, or fetchMode changes.
    // On a fetchMode transition (e.g., Pipeline/Hybrid → Table) clear the
    // visible dataset + refs synchronously so the next paginated fetch starts
    // from empty state — prevents the stale full-loaded dataset from dimming
    // under the table view before the new first-page RPC returns.
    if (prevFetchModeRef.current !== fetchMode) {
      setProspects([]);
      setTotalCount(0);
      setHasMore(true);
      offsetRef.current = 0;
      prospectsRef.current = [];
      prevFetchModeRef.current = fetchMode;
    }
    setOffset(0);
    // This fetch already carries the current search text. A search debounce
    // still pending (typed while the organization or saved view was loading)
    // would only repeat it and dim the results it just loaded.
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    void fetchProspectsRef.current?.(false, 0);
  }, [
    organizationId,
    filters.loanFolder,
    filters.loanOfficerId,
    filters.loanPurpose,
    filters.createdAfter,
    filters.createdBefore,
    filters.minAmount,
    filters.maxAmount,
    filters.pipeline,
    filters.lockStatus,
    filters.deliveryStatus,
    filters.queueStage,
    filters.segment,
    filters.mersStatus,
    fetchMode,
    // Use JSON stringify for array comparison to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.excludedFolders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.excludedMilestones),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.excludedChannels),
    sortConfig.field,
    sortConfig.direction,
    includeTransferred
  ]);

  // Set up real-time subscription (filtered to exclude transferred by default)
  useEffect(() => {
    if (!organizationId) {
return;
}

    log.info('Setting up filtered real-time subscription');

    const channel = supabase
      .channel(`encompass-loans-paginated-${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'encompass_loans',
          // Filter by organization_id at the channel level to reduce event noise
          // Organization never changes for a loan, so this is safe and reduces client load
          // Other filters (folder, etc.) still handled client-side for departure events
          filter: `organization_id=eq.${organizationId}`
        },
        (payload) => {
          log.info('Real-time update received', {
            event: payload.eventType,
            folder: (payload.new as ProspectLoan)?.loan_folder || (payload.old as ProspectLoan)?.loan_folder
          });

          const newLoan = payload.new as ProspectLoan;
          const oldLoan = payload.old as ProspectLoan;

          // Guard against oversized Realtime payloads (>1MB) which Supabase
          // delivers as empty objects. Without this, matchesFilter would evaluate
          // against {}, leading to incorrect filter results.
          if (payload.eventType !== 'DELETE' && (!newLoan || !newLoan.id)) {
            log.warn('Empty Realtime payload (likely >1MB record), scheduling refetch', {
              event: payload.eventType,
              oldId: oldLoan?.id
            });
            scheduleRealtimeRefetch();
            return;
          }

          // When queueStage is active, raw realtime payloads lack joined fields
          // (investor_conditions_status, first_pa_date, recon_status) needed for
          // client-side queue stage classification. Prefer authoritative RPC refetch.
          if (filtersRef.current.queueStage) {
            scheduleRealtimeRefetch();
            return;
          }

          // Check if loan belongs to current organization
          if (newLoan?.organization_id !== organizationId && oldLoan?.organization_id !== organizationId) {
            return;
          }

          // Delegate to the exported pure function for testability
          const matchesFilter = (loan: ProspectLoan) =>
            matchesProspectFilter(loan, filtersRef.current);

          if (payload.eventType === 'INSERT') {
            if (matchesFilter(newLoan)) {
              // Schedule coalesced silent refetch to maintain sort order
              // Uses debounce + maxWait to prevent fetch storms under heavy webhook load
              log.info('New loan matches filters, scheduling silent refetch', {
                loanId: newLoan.id,
                loanNumber: newLoan.trans_details_loan_number
              });
              scheduleRealtimeRefetch();
            }
          } else if (payload.eventType === 'UPDATE') {
            setProspects(prev => {
              const currentIds = new Set(prev.map(p => p.id));
              const action = resolveRealtimeUpdate(newLoan, currentIds, matchesFilter);

              switch (action.type) {
                case 'refetch':
                  scheduleRealtimeRefetch();
                  return prev;
                case 'remove':
                  setTotalCount(c => Math.max(0, c - 1));
                  setOffset(o => Math.max(0, o - 1));
                  return prev.filter(p => p.id !== action.loanId);
                case 'noop':
                  return prev;
              }
            });
          } else if (payload.eventType === 'DELETE') {
            setProspects(prev => {
              const wasInList = prev.some(p => p.id === oldLoan.id);
              if (wasInList) {
                setTotalCount(c => Math.max(0, c - 1));
                setOffset(o => Math.max(0, o - 1)); // Adjust offset to prevent duplicates on load more
                return prev.filter(p => p.id !== oldLoan.id);
              }
              return prev;
            });
          }
        }
      )
      .subscribe();

    // ── Targeted subscriptions for post-closing stepper data ──────────────
    // Only refetch when the changed encompass_loan_id is in the loaded set

    const trackingChannel = supabase
      .channel(`post-closing-tracking-${organizationId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'loan_post_closing_tracking',
        filter: `organization_id=eq.${organizationId}`
      }, (payload) => {
        const eid = (payload.new as Record<string, unknown>)?.encompass_loan_id as string
          || (payload.old as Record<string, unknown>)?.encompass_loan_id as string;
        // In the MERS audit, mers_transferred_at is the sort key, so a tracking
        // change can reorder rows that aren't loaded yet — refetch on any org
        // change. Read pipeline from filtersRef (NOT closed-over filters): this
        // effect doesn't re-run on filters.pipeline (see deps below + the
        // filtersRef churn-avoidance note), so a closure value would be stale.
        if (eid && (filtersRef.current.pipeline === 'mers_audit' || loadedEncompassIdsRef.current.has(eid))) {
          scheduleRealtimeRefetch();
        }
      })
      .subscribe();

    const reconChannel = supabase
      .channel(`banker-recons-status-${organizationId}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'banker_recons',
        filter: `organization_id=eq.${organizationId}`
      }, (payload) => {
        const eid = (payload.new as Record<string, unknown>)?.encompass_loan_id as string;
        if (eid && loadedEncompassIdsRef.current.has(eid)) {
          scheduleRealtimeRefetch();
        }
      })
      .subscribe();

    const paChannel = supabase
      .channel(`loan-docs-pa-${organizationId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'loan_documents',
        filter: `organization_id=eq.${organizationId}`
      }, (payload) => {
        const doc = payload.new as { document_type?: string; encompass_loan_id?: string };
        if (doc.document_type === 'purchase_advice' && doc.encompass_loan_id &&
            loadedEncompassIdsRef.current.has(doc.encompass_loan_id)) {
          scheduleRealtimeRefetch();
        }
      })
      .subscribe();

    return () => {
      log.info('Cleaning up real-time subscriptions');
      supabase.removeChannel(channel);
      supabase.removeChannel(trackingChannel);
      supabase.removeChannel(reconChannel);
      supabase.removeChannel(paChannel);
      // Clear any pending realtime refetch timeout
      if (realtimeRefetchTimeoutRef.current) {
        clearTimeout(realtimeRefetchTimeoutRef.current);
        realtimeRefetchTimeoutRef.current = null;
      }
      realtimeRefetchFirstCallRef.current = 0;
    };
  // Note: filters.searchQuery intentionally excluded - we read from filtersRef to avoid
  // subscription churn on every keystroke (debounce only affects fetch, not state updates)
  }, [organizationId, filters.loanFolder, includeTransferred, scheduleRealtimeRefetch]);

  // Optimistic update — patch a single loan's fields in the local list
  const updateProspect = useCallback((loanId: string, updates: Partial<ProspectLoan>) => {
    setProspects(prev => prev.map(p => p.id === loanId ? { ...p, ...updates } : p));
  }, []);

  return {
    prospects,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    totalCount,
    dataVersion,
    loadMore,
    refetch,
    updateProspect
  };
}
